import { fromDisplayWeight } from 'shared/common/domain/weightUnits';

export interface PlacementPlaceInfo {
  index: number;
  row: number | null;
  column: number | null;
}

export interface PackagePlacementValidationInput {
  packageStorageAreaId: string | null;
  occupiedStoragePlaceIds: string[];
  stackLevel: number | null;
  placeById: Map<string, PlacementPlaceInfo>;
}

function isConsecutive(values: number[]): boolean {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted.every((value, i) => i === 0 || value === sorted[i - 1]! + 1);
}

// Contiguous when indices are consecutive or the places fill a complete row/column rectangle.
export function areOccupiedPlacesContiguous(places: PlacementPlaceInfo[]): boolean {
  if (places.length <= 1 || isConsecutive(places.map((place) => place.index))) {
    return true;
  }

  if (places.some((place) => place.row === null || place.column === null)) {
    return false;
  }

  const rows = places.map((place) => place.row!);
  const columns = places.map((place) => place.column!);
  const rowSpan = Math.max(...rows) - Math.min(...rows) + 1;
  const columnSpan = Math.max(...columns) - Math.min(...columns) + 1;
  const cells = new Set(places.map((place) => `${place.row}:${place.column}`));

  return cells.size === places.length && cells.size === rowSpan * columnSpan;
}

export function occupiedPlacesNotContiguous(
  occupiedStoragePlaceIds: string[],
  placeById: Map<string, PlacementPlaceInfo>,
): boolean {
  const places = occupiedStoragePlaceIds.map((id) => placeById.get(id));
  if (places.some((place) => place === undefined)) {
    return false;
  }

  return !areOccupiedPlacesContiguous(places as PlacementPlaceInfo[]);
}

export interface PlaceWeightCheckInput {
  /** Package weight in kilograms. */
  weight: number | null;
  occupiedStoragePlaceIds: string[];
  /** Max weights in tonnes. */
  places: { id: string; maxWeight: number | null }[];
  defaultMaxWeight: number | null;
  otherPackages: { occupiedStoragePlaceIds: string[]; weight: number | null }[];
}

function exceedsPlaceTotal<T extends { occupiedStoragePlaceIds: string[] }>(
  occupiedStoragePlaceIds: string[],
  value: number | null,
  maxForPlace: (placeId: string) => number | null,
  otherPackages: T[],
  otherValue: (pkg: T) => number | null,
): boolean {
  return occupiedStoragePlaceIds.some((placeId) => {
    const max = maxForPlace(placeId);
    if (max === null) {
      return false;
    }

    const otherTotal = otherPackages
      .filter((pkg) => pkg.occupiedStoragePlaceIds.includes(placeId))
      .reduce((sum, pkg) => sum + (otherValue(pkg) ?? 0), 0);

    return (value ?? 0) + otherTotal > max;
  });
}

export function exceedsPlaceMaxWeight(input: PlaceWeightCheckInput): boolean {
  const placeById = new Map(input.places.map((place) => [place.id, place]));

  return exceedsPlaceTotal(
    input.occupiedStoragePlaceIds,
    input.weight,
    (placeId) => {
      const maxWeight = placeById.get(placeId)?.maxWeight ?? input.defaultMaxWeight;
      return maxWeight === null ? null : fromDisplayWeight(maxWeight, 't');
    },
    input.otherPackages,
    (pkg) => pkg.weight,
  );
}

export interface PlaceHeightCheckInput {
  /** Package height in millimeters. */
  height: number | null;
  occupiedStoragePlaceIds: string[];
  /** Max heights in millimeters. */
  places: { id: string; maxHeight: number | null }[];
  defaultMaxHeight: number | null;
  otherPackages: { occupiedStoragePlaceIds: string[]; height: number | null }[];
}

export function exceedsPlaceMaxHeight(input: PlaceHeightCheckInput): boolean {
  const placeById = new Map(input.places.map((place) => [place.id, place]));

  return exceedsPlaceTotal(
    input.occupiedStoragePlaceIds,
    input.height,
    (placeId) => placeById.get(placeId)?.maxHeight ?? input.defaultMaxHeight,
    input.otherPackages,
    (pkg) => pkg.height,
  );
}

export interface PlaceStackCountCheckInput {
  stackLevel: number | null;
  occupiedStoragePlaceIds: string[];
  places: { id: string; maxStackCount: number | null }[];
  defaultMaxStackCount: number | null;
}

export function exceedsPlaceMaxStackCount(input: PlaceStackCountCheckInput): boolean {
  const placeById = new Map(input.places.map((place) => [place.id, place]));
  const requiredStackCount = (input.stackLevel ?? 0) + 1;

  return input.occupiedStoragePlaceIds.some((placeId) => {
    const maxStackCount = placeById.get(placeId)?.maxStackCount ?? input.defaultMaxStackCount;
    return maxStackCount !== null && requiredStackCount > maxStackCount;
  });
}

export interface PlacementOverlapCheckInput {
  stackLevel: number | null;
  occupiedStoragePlaceIds: string[];
  otherPackages: { occupiedStoragePlaceIds: string[]; stackLevel: number | null }[];
}

export function overlapsOtherPackage(input: PlacementOverlapCheckInput): boolean {
  const stackLevel = input.stackLevel ?? 0;

  return input.otherPackages.some((pkg) =>
    (pkg.stackLevel ?? 0) === stackLevel
    && pkg.occupiedStoragePlaceIds.some((placeId) => input.occupiedStoragePlaceIds.includes(placeId)));
}

export function validatePackagePlacement(input: PackagePlacementValidationInput): string[] {
  const errors: string[] = [];

  const hasStorageArea = !!input.packageStorageAreaId;
  const hasPlacementFields =
    input.occupiedStoragePlaceIds.length > 0
    || input.stackLevel !== null;

  if (!hasStorageArea && hasPlacementFields) {
    errors.push('Placement fields require a package storage area.');
  }

  if (hasStorageArea) {
    if (input.occupiedStoragePlaceIds.length === 0) {
      errors.push('At least one occupied storage place is required when a package storage area is selected.');
    }
  }

  if (input.stackLevel !== null && input.stackLevel < 0) {
    errors.push('Stack level cannot be negative.');
  }

  if (input.occupiedStoragePlaceIds.length > 0) {
    const uniquePlaceCount = new Set(input.occupiedStoragePlaceIds).size;
    if (uniquePlaceCount !== input.occupiedStoragePlaceIds.length) {
      errors.push('Occupied storage places must be unique.');
    }

    const unknownPlace = input.occupiedStoragePlaceIds.some((id) => !input.placeById.has(id));
    if (unknownPlace) {
      errors.push('One or more occupied storage places are invalid for the selected package storage area.');
    }
  }

  return errors;
}
